import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { ShieldCheck, Plus, Trash, UploadSimple, Bug, ClipboardText, Warning, X as XIcon } from "@phosphor-icons/react";

const REGULATORS = [
  { id: "UP-FDA",  label: "UP-FDA (India)",        portal: "https://statedrugs.gov.in/SFDA/third-part-licence-verification.html" },
  { id: "BD-DGDA", label: "BD-DGDA (Bangladesh)",  portal: "https://adlrs.dgdabd.com/public" },
  { id: "CDSCO",   label: "CDSCO (India national)", portal: "https://cdsco.gov.in/opencms/opencms/en/State-Drugs-Control/" },
  { id: "Other",   label: "Other",                  portal: null },
];

export default function GovVerify() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [scrapeBusy, setScrapeBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  const load = () => {
    const params = filter ? { regulator: filter } : {};
    return api.get("/gov-verifications", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [filter]);  // eslint-disable-line

  const scrape = async () => {
    setScrapeBusy(true);
    try {
      const { data } = await api.post("/gov-verifications/scrape/bd-dgda");
      alert(`Scraped ${data.captured} BD-DGDA rows from the public portal.`);
      load();
    } catch (e) {
      alert("Scrape failed: " + (e.response?.data?.detail || e.message));
    } finally { setScrapeBusy(false); }
  };

  const importFile = async (file, regulator) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const form = new FormData();
      form.append("regulator", regulator);
      form.append("file", file);
      const { data } = await api.post("/gov-verifications/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert(`Imported ${data.inserted} verification rows.`);
      load();
    } catch (e) {
      alert("Import failed: " + (e.response?.data?.detail || e.message));
    } finally { setImportBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this verification record?")) return;
    await api.delete(`/gov-verifications/${id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="gov-verify-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Compliance</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Government license verifications</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            Track UP-FDA · BD-DGDA · CDSCO license lookups for cases. Manual entry, Excel import, or scrape the public BD-DGDA list.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="add-verify-btn" onClick={() => setShowAdd(true)} className="span-btn">
            <Plus size={12}/> Add manually
          </button>
          <label className="span-btn span-btn-ghost cursor-pointer" data-testid="import-verify-btn">
            <UploadSimple size={12}/> Import CSV/XLSX
            <input
              type="file" accept=".csv,.xlsx" className="hidden"
              onChange={(e) => importFile(e.target.files?.[0], filter || "UP-FDA")}
              disabled={importBusy}
            />
          </label>
          <button data-testid="scrape-dgda-btn" onClick={scrape} disabled={scrapeBusy} className="span-btn span-btn-ghost">
            <Bug size={12}/> {scrapeBusy ? "Scraping…" : "Scrape BD-DGDA"}
          </button>
        </div>
      </div>

      <div className="border-l-4 border-[var(--span-info)] bg-[var(--span-surface-2)] p-3 text-[11px]" data-testid="gov-notice">
        <Warning size={12} className="inline mr-1"/> Neither UP-FDA nor BD-DGDA publishes a machine-readable API.
        SPAN supports three intake modes: manual paste from the official portal, bulk Excel/CSV import (e.g. from
        the regulator's exported reports), and a robots-respecting scrape of the BD-DGDA public expiring-licenses list.
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`badge ${!filter ? "badge-success" : ""}`} data-testid="filter-all">All ({items.length})</button>
        {REGULATORS.map((r) => (
          <button key={r.id} data-testid={`filter-${r.id}`} onClick={() => setFilter(r.id)} className={`badge ${filter === r.id ? "badge-success" : ""}`}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="card-flat">
        <table className="span-table">
          <thead>
            <tr>
              <th>Regulator</th><th>License #</th><th>Firm</th><th>Status</th>
              <th>Validity</th><th>Source</th><th>Captured</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">No verification records yet.</td></tr>
            )}
            {items.map((v) => (
              <tr key={v.verification_id} data-testid={`verify-row-${v.verification_id}`}>
                <td><span className="badge">{v.regulator}</span></td>
                <td className="font-mono text-[11px]">{v.license_number}</td>
                <td className="font-medium">{v.firm_name || "—"}</td>
                <td>
                  <span className={`badge ${v.status === "valid" ? "badge-success" : v.status === "expired" || v.status === "cancelled" ? "badge-danger" : ""}`}>
                    {v.status || "—"}
                  </span>
                </td>
                <td className="font-mono text-[11px]">{v.validity_date || "—"}</td>
                <td className="text-[11px] text-[var(--span-text-2)] font-mono">{v.source}</td>
                <td className="text-[var(--span-text-2)]">{fmtDate(v.created_at)}</td>
                <td>
                  <button onClick={() => remove(v.verification_id)} className="text-[var(--span-text-2)] hover:text-[var(--span-danger)]" data-testid={`delete-verify-${v.verification_id}`}>
                    <Trash size={13}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    regulator: "UP-FDA", license_number: "", firm_name: "", firm_address: "",
    validity_date: "", status: "valid", case_id: "", raw_excerpt: "", portal_url: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.license_number) return;
    setBusy(true);
    try {
      await api.post("/gov-verifications", {
        ...form,
        case_id: form.case_id || null,
        portal_url: form.portal_url || (REGULATORS.find((r) => r.id === form.regulator)?.portal),
      });
      onSaved();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-verify-modal">
      <form onSubmit={submit} className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow flex items-center gap-1"><ClipboardText size={12}/> New verification record</div>
          <button type="button" onClick={onClose}><XIcon size={16}/></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Regulator</label>
              <select value={form.regulator} onChange={(e) => set("regulator", e.target.value)} className="span-input" data-testid="verify-regulator">
                {REGULATORS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="span-input">
                <option>valid</option><option>expired</option><option>suspended</option><option>cancelled</option><option>unknown</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">License number *</label>
            <input data-testid="verify-license" value={form.license_number} onChange={(e) => set("license_number", e.target.value)} className="span-input font-mono"/>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Firm name</label>
            <input value={form.firm_name} onChange={(e) => set("firm_name", e.target.value)} className="span-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Firm address</label>
              <input value={form.firm_address} onChange={(e) => set("firm_address", e.target.value)} className="span-input"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Validity date</label>
              <input type="date" value={form.validity_date} onChange={(e) => set("validity_date", e.target.value)} className="span-input"/>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Link to case (optional)</label>
            <input value={form.case_id} onChange={(e) => set("case_id", e.target.value)} placeholder="case_…" className="span-input font-mono"/>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Raw portal excerpt (paste from portal)</label>
            <textarea value={form.raw_excerpt} onChange={(e) => set("raw_excerpt", e.target.value)} rows={3} className="span-input font-mono text-[11px]"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button disabled={busy} data-testid="submit-verify" className="span-btn"><ShieldCheck size={12}/> Save record</button>
        </div>
      </form>
    </div>
  );
}
